import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { PageHeader, Card, Loading, PrimaryBtn, GhostBtn, Field, Input } from "../components/kit";

const FIELDS = [
  ["score", "Score"], ["putts", "Putts"], ["three_putts", "3-Putts"], ["penalties", "Penalties"],
  ["fairways_hit", "Fairways"], ["gir", "GIR"], ["up_down_attempts", "U/D attempts"], ["up_down_success", "U/D made"],
];

export default function Rounds() {
  const [rounds, setRounds] = useState(null);
  const [form, setForm] = useState({ course_par: 72 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/rounds").then((r) => setRounds(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.score) { toast.error("Enter a score"); return; }
    setBusy(true);
    try {
      const payload = { course_par: Number(form.course_par) || 72 };
      FIELDS.forEach(([k]) => { if (form[k] !== undefined && form[k] !== "") payload[k] = Number(form[k]); });
      await api.post("/rounds", payload);
      toast.success("Round saved");
      setForm({ course_par: 72 });
      await load();
    } catch { toast.error("Could not save round"); }
    finally { setBusy(false); }
  };

  const regen = async () => {
    toast.loading("Re-analyzing & adjusting your plan…", { id: "r" });
    try { await api.post("/plan/generate"); toast.success("Plan updated with your new data!", { id: "r" }); }
    catch { toast.error("Update failed", { id: "r" }); }
  };

  if (!rounds) return <div className="p-6"><Loading /></div>;

  return (
    <div className="p-5 sm:p-8 max-w-4xl mx-auto">
      <PageHeader eyebrow="Performance" title="Rounds"
        right={<GhostBtn onClick={regen} data-testid="rounds-regen">Update Plan</GhostBtn>} />

      <Card className="p-6 mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00] mb-4">Log a round</div>
        <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Course par"><Input type="number" value={form.course_par ?? ""} onChange={(e) => setForm({ ...form, course_par: e.target.value })} /></Field>
          {FIELDS.map(([k, label]) => (
            <Field key={k} label={label}>
              <Input data-testid={`round-${k}`} type="number" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </Field>
          ))}
          <div className="col-span-2 sm:col-span-4">
            <PrimaryBtn data-testid="round-save" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Round"}</PrimaryBtn>
          </div>
        </form>
        <p className="text-xs text-[#A3A3A3] mt-3">After adding rounds, tap <span className="text-[#CCFF00]">Update Plan</span> so the AI re-prioritizes your training.</p>
      </Card>

      <div className="space-y-3">
        {rounds.length === 0 && <p className="text-[#A3A3A3] text-sm">No rounds yet.</p>}
        {rounds.map((r) => (
          <Card key={r.id} className="p-4 flex items-center gap-4 flex-wrap">
            <div className="text-center pr-4 border-r border-[#262626]">
              <div className="font-heading text-3xl text-[#CCFF00]">{r.score}</div>
              <div className="text-[10px] uppercase text-[#A3A3A3]">{r.is_tournament ? "Tournament" : "Round"}</div>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              {[["Putts", r.putts], ["3-Putts", r.three_putts], ["Pen", r.penalties], ["FW", r.fairways_hit], ["GIR", r.gir]].map(([l, v]) => v != null && (
                <span key={l} className="text-[#A3A3A3]">{l} <span className="text-white font-bold">{v}</span></span>
              ))}
            </div>
            <div className="ml-auto text-xs text-[#666]">{r.played_date}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
